import type { Message, AgentMessageFragment } from "@/hooks/use-stream-conversation";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";

export type GroupedMessage =
    | { type: "user"; message: Message }
    | { type: "agent"; message: Message }
    | {
          type: "todo-group";
          todoId: string;
          todoTitle: string;
          todoStatus: AgentResponseBlock_TodoItem_Phase;
          fragments: AgentMessageFragment[];
      }
    | { type: "agent-ungrouped"; fragments: AgentMessageFragment[]; messageId: number };

/**
 * Groups messages by todo items. All agent response fragments between a todo item
 * and the next todo item (or user input) are grouped together.
 *
 * Agent responses without todos are rendered directly in the conversation.
 *
 * Note: This grouping is controlled by the TodoGroupingEnabled feature flag.
 * When disabled, all messages are rendered as ungrouped in the conversation.
 *
 * @param messages - The messages to group
 */
export function groupMessagesByTodo(messages: Message[]): GroupedMessage[] {
    const grouped: GroupedMessage[] = [];
    let currentTodoGroup: {
        todoId: string;
        todoTitle: string;
        todoStatus: AgentResponseBlock_TodoItem_Phase;
        fragments: AgentMessageFragment[];
    } | null = null;
    let ungroupedFragments: AgentMessageFragment[] = [];
    let currentMessageId = 0;

    const flushUngrouped = () => {
        if (ungroupedFragments.length > 0) {
            grouped.push({ type: "agent-ungrouped", fragments: [...ungroupedFragments], messageId: currentMessageId });
            ungroupedFragments = [];
        }
    };

    const flushTodoGroup = () => {
        if (currentTodoGroup) {
            grouped.push({ type: "todo-group", ...currentTodoGroup });
            currentTodoGroup = null;
        }
    };

    for (const message of messages) {
        if (message.type === "user") {
            // Flush any pending groups
            flushUngrouped();
            flushTodoGroup();
            // Add user message
            grouped.push({ type: "user", message });
            continue;
        }

        // Agent message
        const agentMessage = message;
        currentMessageId = agentMessage.id;

        for (const fragment of agentMessage.fragments) {
            // Check if this fragment is a todo item with IN_PROGRESS status
            if (fragment.type === "todoItem" && fragment.status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS) {
                // Flush any ungrouped fragments before starting new todo
                flushUngrouped();
                // Flush previous todo group if exists
                flushTodoGroup();
                // Start new todo group
                currentTodoGroup = {
                    todoId: fragment.todoItemId,
                    todoTitle: fragment.title,
                    todoStatus: fragment.status,
                    fragments: [],
                };
            } else if (fragment.type === "todoItem" && fragment.status === AgentResponseBlock_TodoItem_Phase.DONE) {
                // Update status of current todo group if it matches
                if (currentTodoGroup && currentTodoGroup.todoId === fragment.todoItemId) {
                    currentTodoGroup.todoStatus = fragment.status;
                    // Flush the completed todo group
                    flushTodoGroup();
                }
            } else if (fragment.type !== "todoItem") {
                // Skip text fragments that are just announcing todo state changes
                // These are system messages like "Marking 1 todo item as in progress"
                const isSystemTodoMessage =
                    fragment.type === "text" &&
                    fragment.content &&
                    fragment.content.toLowerCase().includes("marking") &&
                    fragment.content.toLowerCase().includes("todo") &&
                    (fragment.content.toLowerCase().includes("in progress") ||
                        fragment.content.toLowerCase().includes("done"));

                if (isSystemTodoMessage) {
                    // Skip this fragment - it's just a system message about todo state
                    continue;
                }

                // Check if this is a summary - summaries should never be grouped into todos
                const summaryRegex = /^#+\s*summary(\s|\n|$)/i;
                const isSummary = fragment.type === "text" && summaryRegex.exec(fragment.content) !== null;

                if (isSummary) {
                    // Flush any active todo group before the summary
                    flushTodoGroup();
                    // Add summary to ungrouped
                    ungroupedFragments.push(fragment);
                } else {
                    // Regular fragment (not a todo item or summary)
                    if (currentTodoGroup) {
                        // Add to current todo group
                        currentTodoGroup.fragments.push(fragment);
                    } else {
                        // No active todo group, add to ungrouped
                        ungroupedFragments.push(fragment);
                    }
                }
            }
        }
    }

    // Flush any remaining fragments
    flushUngrouped();
    flushTodoGroup();

    return grouped;
}
